import { useJobStore } from "../../stores/jobStore";
import { ChartOut } from "../../api/client";

// ── Stage result section ───────────────────────────────────────────────────

function Section({ title, data, color = "text-slate-300" }: {
  title: string;
  data: Record<string, unknown> | null | undefined;
  color?: string;
}) {
  if (!data) return null;

  const entries = Object.entries(data).filter(([, v]) => v != null);
  if (!entries.length) return null;

  return (
    <div className="border border-rdp-border rounded-lg overflow-hidden">
      <div className="px-3 py-1.5 bg-rdp-hover border-b border-rdp-border">
        <p className={`text-[10px] font-semibold uppercase tracking-widest ${color}`}>{title}</p>
      </div>
      <div className="px-3 py-2 space-y-1">
        {entries.map(([k, v]) => (
          <div key={k} className="flex items-start justify-between gap-2">
            <span className="text-[10px] text-slate-500 font-mono flex-shrink-0">{k}</span>
            <span className="text-[10px] text-slate-300 font-mono text-right break-all">
              {typeof v === "number"
                ? v.toFixed(3)
                : Array.isArray(v) || typeof v === "object"
                ? JSON.stringify(v)
                : String(v)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Confidence bar ─────────────────────────────────────────────────────────

function ConfBar({ value }: { value: number | null | undefined }) {
  if (value == null) return null;
  const pct = Math.round(value * 100);
  const color = value >= 0.85 ? "bg-emerald-500" : value >= 0.65 ? "bg-amber-500" : "bg-red-500";
  const textColor = value >= 0.85 ? "text-emerald-400" : value >= 0.65 ? "text-amber-400" : "text-red-400";

  return (
    <div className="space-y-1">
      <div className="flex justify-between text-[10px]">
        <span className="text-slate-500">Pipeline confidence</span>
        <span className={`font-mono font-bold ${textColor}`}>{pct}%</span>
      </div>
      <div className="h-1.5 bg-rdp-border rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-500 ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

// ── Flat triage details (nested under details key) ─────────────────────────

function TriageSection({ data }: { data: Record<string, unknown> | null | undefined }) {
  if (!data) return null;
  const { details, ...top } = data as { details?: Record<string, unknown>; [k: string]: unknown };
  const merged = { ...top, ...(details ?? {}) };
  return <Section title="Triage" data={merged} color="text-blue-400" />;
}

// ── Main panel ────────────────────────────────────────────────────────────

export function ChartDetailPanel() {
  const { selectedChart, selectChart, clearHITL } = useJobStore();

  if (!selectedChart) return null;

  const chart: ChartOut = selectedChart;
  const isHITL = chart.status === "NEEDS_REVIEW";
  const canOpenWPD = ["NEEDS_REVIEW", "COMPLETED", "AUTO_APPROVED"].includes(chart.status);

  return (
    <aside className="w-72 flex-shrink-0 border-l border-rdp-border bg-rdp-bg flex flex-col overflow-hidden animate-slide-up">
      {/* Header */}
      <div className="px-3 py-2.5 border-b border-rdp-border flex items-center justify-between">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
          Page {chart.page_num + 1} · Chart {chart.chart_idx + 1}
        </p>
        <button
          onClick={clearHITL}
          className="text-slate-600 hover:text-slate-300 text-xs leading-none cursor-pointer"
        >
          ✕
        </button>
      </div>

      {/* Image preview */}
      <div className="flex-shrink-0 bg-rdp-border/20 border-b border-rdp-border flex items-center justify-center" style={{ minHeight: 160 }}>
        {chart.thumbnail_s3_key || chart.image_s3_key ? (
          <img
            src={`/media/jobs/${chart.job_id}/charts/${chart.id}/thumbnail`}
            alt={`Chart P${chart.page_num + 1}`}
            className="object-contain max-h-48 w-full"
          />
        ) : (
          <div className="flex flex-col items-center gap-1 text-rdp-border py-8">
            <span className="text-2xl">📈</span>
            <span className="text-[10px] text-slate-600">No image yet</span>
          </div>
        )}
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
        {/* Confidence */}
        <ConfBar value={chart.pipeline_confidence} />

        {/* WPD button */}
        {canOpenWPD && (
          <button
            onClick={() => selectChart(chart)}
            className={`w-full text-xs py-2 flex items-center justify-center gap-2 rounded-md font-medium transition-colors ${
              isHITL
                ? "btn-primary"
                : "border border-rdp-border text-slate-400 hover:text-slate-200 hover:border-slate-500"
            }`}
          >
            <span>{isHITL ? "Review in WebPlotDigitizer" : "Open in WebPlotDigitizer"}</span>
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </button>
        )}

        {/* Gate failure */}
        {chart.gate_failure && (
          <Section title="Gate Failure" data={chart.gate_failure as Record<string, unknown>} color="text-red-400" />
        )}

        {/* Stage results */}
        <TriageSection data={chart.triage_result as Record<string, unknown>} />
        <Section title="Detection" data={chart.detection_result as Record<string, unknown>} color="text-violet-400" />
        <Section title="OCR" data={chart.ocr_result as Record<string, unknown>} color="text-cyan-400" />
        <Section title="Calibration" data={chart.calibration_result as Record<string, unknown>} color="text-emerald-400" />
        <Section title="Extraction" data={chart.extraction_result as Record<string, unknown>} color="text-emerald-300" />

        {/* Dimensions */}
        {(chart.image_width_px || chart.image_height_px) && (
          <div className="text-[10px] text-slate-600 font-mono">
            {chart.image_width_px} × {chart.image_height_px} px
            {chart.total_latency_ms && ` · ${chart.total_latency_ms}ms`}
          </div>
        )}
      </div>
    </aside>
  );
}
